import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { LatLngBoundsLiteral, LatLngTuple } from 'leaflet';
import type { Report } from '../features/reports/report';

export interface MapState {
  mode: 'list' | 'detail';
  center: LatLngTuple;
  zoom: number;
  minZoom: number;
  maxZoom: number;
  bounds: LatLngBoundsLiteral | null;
  detailReportId: string | null;
  detailLocation?: [number, number] | null;
}

const defaultCenter: LatLngTuple = [41.8719, 12.5674];
const defaultZoom = 6;
const defaultMinZoom = 6;
const defaultMaxZoom = 18;
const defaultBounds: LatLngBoundsLiteral = [
  [35.288961, 6.627265],
  [47.092146, 18.784444],
];

const initialState: MapState = {
  mode: 'list',
  center: defaultCenter,
  zoom: defaultZoom,
  minZoom: defaultMinZoom,
  maxZoom: defaultMaxZoom,
  bounds: defaultBounds,
  detailReportId: null,
  detailLocation: null,
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    enterList: (): MapState => ({
      mode: 'list',
      center: [...defaultCenter] as LatLngTuple,
      zoom: defaultZoom,
      minZoom: defaultMinZoom,
      maxZoom: defaultMaxZoom,
      bounds: defaultBounds.map((b) => [...b]) as LatLngBoundsLiteral,
      detailReportId: null,
      detailLocation: null,
    }),
    enterDetail: (_state, action: PayloadAction<Report>): MapState => {
      const report = action.payload;

      const [lat, lng] =
        Array.isArray(report.location) && report.location.length === 2
          ? report.location
          : defaultCenter;

      const delta = 0.1;

      return {
        mode: 'detail',
        center: [lat, lng],
        zoom: 12,
        minZoom: 10,
        maxZoom: 16,
        bounds: [
          [lat - delta, lng - delta],
          [lat + delta, lng + delta],
        ],
        detailReportId: report.id,
        detailLocation: [lat, lng],
      };
    },
  },
});

export const { enterList, enterDetail } = mapSlice.actions;
export default mapSlice.reducer;
